/**
 * RhProfilsPanel — profils horaires agrégés (LOT E).
 * Vue de contrôle RH : tous les profils des briques, conformité légale, gouvernance.
 * Conforme mockup_rh_profils_horaires.
 */
import { useState, useEffect } from 'react';
import { Card, Alert, Badge, Button, Field } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';
import { RhServicePill } from './rh-ui';
import { PeriodesSaisonnieresPanel } from '@/components/grc/PeriodesSaisonnieresPanel';
import { Sun } from 'lucide-react';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import type { RhProfilAgrege } from '@roronoa-collectivites/shared-types';

const JOURS = ['', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

/**
 * Couleur des heures/semaine par rapport au temps plein (35h).
 * Vert : 34–36h (plein). Orange : écart modéré (30–34 ou 36–40).
 * Rouge : écart fort (< 30 ou > 40).
 */
function heuresColor(h: number): string {
  const ecart = Math.abs(h - 35);
  if (ecart <= 1) return 'var(--c-success, #3fae74)';
  if (ecart <= 5) return 'var(--c-warn, #d99a28)';
  return 'var(--c-danger, #d8583a)';
}

export function RhProfilsPanel() {
  const [profils, setProfils] = useState<RhProfilAgrege[]>([]);
  const [gouvernance, setGouvernance] = useState<boolean>(true);
  const [filtreService, setFiltreService] = useState('');
  const [filtreConf, setFiltreConf] = useState<'tous' | 'conformes' | 'non'>('tous');
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<RhProfilAgrege | null>(null);
  const [creation, setCreation] = useState(false);

  async function charger() {
    setLoading(true);
    try {
      const [p, g] = await Promise.all([api.rh.profilsAgreges(), api.rh.profilsGouvernance()]);
      setProfils(p); setGouvernance(g.modifiablesParBriques);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void charger(); }, []);

  async function changerGouvernance(val: boolean) {
    setError(null); setMsg(null);
    try {
      await api.rh.setProfilsGouvernance(val);
      setGouvernance(val);
      setMsg(val ? 'Les briques peuvent gérer leurs profils.' : 'Profils verrouillés : la RH gère seule.');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  const services = [...new Set(profils.map((p) => p.service))].sort();
  const filtres = profils.filter((p) =>
    (!filtreService || p.service === filtreService) &&
    (filtreConf === 'tous' || (filtreConf === 'conformes' ? p.conforme : !p.conforme))
  );
  const nbConf = profils.filter((p) => p.conforme).length;
  const nbNon = profils.length - nbConf;

  return (
    <div className="space-y-5">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <div className="rounded-r-lg border border-border bg-rh-tint/40 px-4 py-3 text-sm text-ink-muted">
        Tous les profils horaires de chaque service, réunis pour contrôle. Un profil non conforme (durée &gt; 10h, ou &gt; 6h sans pause) est signalé. La RH peut verrouiller la modification depuis les briques.
      </div>

      {/* Item 6.5 — les périodes d'horaires saisonniers vivent ICI, avec les
          profils qu'elles concernent (recette Henrico 02/08 : « il serait tout
          à fait naturel dans les profils horaires » — il l'était pas dans le
          GRC). L'affichage public des horaires d'été, lui, reste dans
          GRC → Infos pratiques, sur chaque fiche de service. */}
      <div className="rounded-r-lg border border-border bg-surface/50 p-4">
        <div className="mb-3 flex items-center gap-2">
          <Sun className="h-5 w-5 text-rh" aria-hidden="true" />
          <h3 className="font-medium text-ink">Périodes d'horaires saisonniers</h3>
        </div>
        <PeriodesSaisonnieresPanel />
      </div>

      {/* Stats */}
      <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))' }}>
        <StatBox label="Profils au total" value={profils.length} />
        <StatBox label="Conformes" value={nbConf} tone="ok" />
        <StatBox label="À vérifier" value={nbNon} tone={nbNon > 0 ? 'warn' : undefined} />
        <StatBox label="Services couverts" value={services.length} />
      </div>

      {/* Gouvernance */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-ink">Gouvernance des profils</h3>
            <p className="text-xs text-ink-subtle">Qui peut créer / modifier / supprimer les profils dans chaque brique.</p>
          </div>
          <div className="inline-flex rounded-r-lg border border-border-hi bg-surface/60 p-0.5">
            <button type="button" onClick={() => void changerGouvernance(true)} className={`rounded-r-md px-3 py-1.5 text-xs ${gouvernance ? 'bg-rh text-white' : 'text-ink-muted'}`}>Les briques gèrent</button>
            <button type="button" onClick={() => void changerGouvernance(false)} className={`rounded-r-md px-3 py-1.5 text-xs ${!gouvernance ? 'bg-rh text-white' : 'text-ink-muted'}`}>Lecture seule — RH gère</button>
          </div>
        </div>
        {!gouvernance && (
          <div className="mt-3 flex items-center gap-2 rounded-r-lg border border-border bg-surface/40 px-3 py-2 text-xs text-ink-muted">
            🔒 Modification verrouillée. Dans chaque brique, les profils restent utilisables pour générer les plannings, mais création / modification / suppression sont désactivées (message « Profils gérés par la RH »).
          </div>
        )}
      </Card>

      {/* Liste agrégée */}
      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <h3 className="font-display text-sm font-semibold text-ink">Profils par service</h3>
          <div className="ml-auto flex gap-2">
            <select value={filtreService} onChange={(e) => setFiltreService(e.target.value)} className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink">
              <option value="">Tous les services</option>
              {services.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="inline-flex rounded-r-md border border-border-hi bg-surface/60 p-0.5">
              {(['tous', 'conformes', 'non'] as const).map((f) => (
                <button key={f} type="button" onClick={() => setFiltreConf(f)} className={`rounded-r-sm px-2.5 py-1 text-xs ${filtreConf === f ? 'bg-rh text-white' : 'text-ink-muted'}`}>{f === 'tous' ? 'Tous' : f === 'conformes' ? 'Conformes' : 'Non conformes'}</button>
              ))}
            </div>
            <Button variant="rh" size="sm" onClick={() => setCreation(true)}>＋ Créer un profil (RH)</Button>
          </div>
        </div>
        {loading ? <p className="px-4 py-5 text-sm text-ink-muted">Chargement…</p> :
         filtres.length === 0 ? <p className="px-4 py-5 text-center text-sm text-ink-subtle">Aucun profil pour ce filtre.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-r-eyebrow text-ink-subtle">
                  <th className="px-4 py-2.5 font-medium">Service</th>
                  <th className="px-4 py-2.5 font-medium">Profil</th>
                  <th className="px-4 py-2.5 font-medium">Plages</th>
                  <th className="px-4 py-2.5 text-right font-medium">H/sem.</th>
                  <th className="px-4 py-2.5 font-medium">Conformité</th>
                  <th className="px-4 py-2.5 text-right font-medium">Détail</th>
                </tr>
              </thead>
              <tbody>
                {filtres.map((p) => (
                  <tr key={p.id} className="border-b border-border/60 last:border-0 align-top">
                    <td className="px-4 py-2.5"><RhServicePill service={p.service} /></td>
                    <td className="px-4 py-2.5 font-medium text-ink">{p.nom}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {p.plages.slice(0, 6).map((pl, i) => (
                          <span key={i} className={`rounded-r-sm border px-1.5 py-0.5 text-[11px] ${!p.conforme ? 'border-danger/40 text-danger' : 'border-border-hi text-ink-muted'}`}>{JOURS[pl.jour]} {pl.debut}–{pl.fin}</span>
                        ))}
                        {p.plages.length > 6 && <span className="text-[11px] text-ink-subtle">+{p.plages.length - 6}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums" style={{ color: heuresColor(p.heuresSemaine) }}>{p.heuresSemaine}</td>
                    <td className="px-4 py-2.5">
                      {p.conforme
                        ? <Badge variant="success"><CheckCircle2 className="mr-1 inline h-3 w-3" />Conforme</Badge>
                        : <span title={p.motifs.join(' · ')}><Badge variant="urgent"><AlertTriangle className="mr-1 inline h-3 w-3" />{p.motifs[0] ?? 'Non conforme'}</Badge></span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button type="button" onClick={() => setDetail(p)} className={`rounded-r-sm border border-border-hi px-2 py-1 text-[11px] ${p.conforme ? 'text-ink-muted hover:text-ink' : 'text-danger hover:text-danger'}`}>{p.conforme ? 'Voir' : 'Corriger'}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Détail d'un profil (Voir / Corriger) */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} title={detail ? `Profil — ${detail.nom}` : ''}
        footer={<Button variant="ghost" onClick={() => setDetail(null)}>Fermer</Button>}>
        {detail && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <RhServicePill service={detail.service} />
              <span className="text-sm text-ink-muted">{detail.heuresSemaine} h/semaine</span>
              {detail.conforme
                ? <Badge variant="success"><CheckCircle2 className="mr-1 inline h-3 w-3" />Conforme</Badge>
                : <Badge variant="urgent"><AlertTriangle className="mr-1 inline h-3 w-3" />Non conforme</Badge>}
            </div>
            {!detail.conforme && detail.motifs.length > 0 && (
              <Alert variant="warning">
                <strong>À corriger :</strong>
                <ul className="mt-1 list-disc pl-5 text-sm">
                  {detail.motifs.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
                <p className="mt-2 text-xs">La correction se fait dans la brique concernée ({detail.service}) via l'écran « Profils & génération ».</p>
              </Alert>
            )}
            <div>
              <div className="mb-1.5 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Plages hebdomadaires</div>
              <div className="flex flex-wrap gap-1.5">
                {detail.plages.map((pl, i) => (
                  <span key={i} className={`rounded-r-md border px-2 py-1 text-xs ${!detail.conforme ? 'border-danger/40 text-danger' : 'border-border-hi text-ink-muted'}`}>{JOURS[pl.jour]} {pl.debut}–{pl.fin}{pl.titre ? ` · ${pl.titre}` : ''}</span>
                ))}
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* Créer un profil (RH) */}
      <CreerProfilModal open={creation} onClose={() => setCreation(false)} onDone={() => { setCreation(false); void charger(); }} services={['service_technique', 'scolaire_cantine', 'grc', 'petite_enfance', 'urbanisme', 'comptable']} />
    </div>
  );
}

const JOURS_OPTS = [{ v: 1, l: 'Lundi' }, { v: 2, l: 'Mardi' }, { v: 3, l: 'Mercredi' }, { v: 4, l: 'Jeudi' }, { v: 5, l: 'Vendredi' }, { v: 6, l: 'Samedi' }, { v: 7, l: 'Dimanche' }];
const SVC_LABEL: Record<string, string> = { service_technique: 'Service technique', scolaire_cantine: 'Scolaire / cantine', grc: 'Communication / GRC', petite_enfance: 'Petite enfance', urbanisme: 'Urbanisme', comptable: 'Comptabilité' };

function CreerProfilModal({ open, onClose, onDone, services }: { open: boolean; onClose: () => void; onDone: () => void; services: string[] }) {
  const [service, setService] = useState(services[0]!);
  const [nom, setNom] = useState('');
  const [plages, setPlages] = useState<Array<{ jour: number; debut: string; fin: string }>>([]);
  const [jour, setJour] = useState(1);
  const [debut, setDebut] = useState('08:00');
  const [fin, setFin] = useState('12:00');
  const [conf, setConf] = useState<{ conforme: boolean; motifs: string[]; heuresSemaine: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (plages.length === 0) { setConf(null); return; }
    api.rh.verifierProfil(plages).then(setConf).catch(() => setConf(null));
  }, [plages]);

  function ajouterPlage() { setPlages((p) => [...p, { jour, debut, fin }]); }
  function retirer(i: number) { setPlages((p) => p.filter((_, idx) => idx !== i)); }

  async function enregistrer() {
    setSaving(true); setError(null);
    try { await api.rh.creerProfilRh(service, nom, plages); onDone(); setNom(''); setPlages([]); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Créer un profil horaire"
      footer={<><Button variant="ghost" onClick={onClose}>Annuler</Button><Button variant="rh" loading={saving} disabled={nom.trim().length < 2 || plages.length === 0} onClick={() => void enregistrer()}>Enregistrer le profil</Button></>}>
      <div className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}
        <Field label="Nom du profil" htmlFor="cp-nom"><input id="cp-nom" type="text" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Ex. Agent voirie" className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink" /></Field>
        <Field label="Service" htmlFor="cp-svc">
          <select id="cp-svc" value={service} onChange={(e) => setService(e.target.value)} className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
            {services.map((s) => <option key={s} value={s}>{SVC_LABEL[s] ?? s}</option>)}
          </select>
        </Field>
        <div>
          <div className="mb-1.5 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Plages horaires</div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <select value={jour} onChange={(e) => setJour(Number(e.target.value))} className="h-9 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
              {JOURS_OPTS.map((j) => <option key={j.v} value={j.v}>{j.l}</option>)}
            </select>
            <input type="time" value={debut} onChange={(e) => setDebut(e.target.value)} className="h-9 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            <span className="text-ink-muted">→</span>
            <input type="time" value={fin} onChange={(e) => setFin(e.target.value)} className="h-9 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            <Button variant="secondary" size="sm" onClick={ajouterPlage}>＋ Ajouter</Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {plages.map((pl, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-r-md border border-border-hi px-2 py-1 text-xs text-ink-muted">
                {JOURS_OPTS.find((j) => j.v === pl.jour)?.l.slice(0, 3)} {pl.debut}–{pl.fin}
                <button type="button" onClick={() => retirer(i)} className="text-ink-subtle hover:text-danger">✕</button>
              </span>
            ))}
          </div>
        </div>
        {conf && (
          <div className="space-y-1.5">
            <div className={`flex items-center gap-2 rounded-r-md px-3 py-2 text-sm ${conf.conforme ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
              {conf.conforme ? <><CheckCircle2 className="h-4 w-4" /> Conforme — {conf.heuresSemaine} h/semaine</> : <><AlertTriangle className="h-4 w-4" /> Non conforme</>}
            </div>
            {!conf.conforme && <ul className="list-disc pl-5 text-xs text-danger">{conf.motifs.map((m, i) => <li key={i}>{m}</li>)}</ul>}
            <p className="text-xs text-ink-subtle">Un profil non conforme peut être enregistré mais reste signalé tant qu'il n'est pas corrigé.</p>
          </div>
        )}
      </div>
    </Dialog>
  );
}

function StatBox({ label, value, tone }: { label: string; value: number; tone?: 'ok' | 'warn' }) {
  const c = tone === 'ok' ? 'text-success' : tone === 'warn' ? 'text-warn' : 'text-ink';
  return <div className="rounded-r-lg border border-border bg-surface px-3.5 py-3"><div className="text-[11px] text-ink-subtle">{label}</div><div className={`mt-0.5 text-2xl font-bold ${c}`}>{value}</div></div>;
}
